// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { ModalBody, ComposedModal, Button, Loading } from '@carbon/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';

import ModalManager from 'Containers/ModalManager/ModalManager';
import { NWTextInput, NWNotification, NWComboBox } from 'Components/shared';
import { NETWORK_ONE_DISTRIBUTION } from 'Constants';

import LoginBackground from 'Assets/images/login-bg2.jpg';
import Logo from 'Assets/images/logo-nod.png';
import Logo2x from 'Assets/images/logo-nod@2x.png';

const StyledComposedModal = styled(ComposedModal)`
  width: 100%;
  height: 100%;
  z-index: 10000 !important;
  background: #0F233C no-repeat center url(${ LoginBackground });
  background-size: cover;

  .cds--inline-notification {
    background-color: transparent;
    color: #3A3A3A;
    max-width: 100%;
    border: none;

    .cds--inline-notification__details {
      margin: 0;
    }

    &.cds--inline-notification--error {
      background: #FFF2F1;
    }

    p {
      padding-right: 0;
      font-size: 14px;
    }

    .cds--inline-notification__close-button .cds--inline-notification__close-icon {
      fill: #3A3A3A;
    }
  }

  .responseMessage {
    p {
      padding-right: 0;
    }

    .messages .message {
      padding: 10px;
    }
  }

  .submit {
    width: 100%;
    height: 40px;
    max-width: none;
    margin-top: 24px;
    padding: 0;
    justify-content: center;
    font-size: 1rem;
    text-align: center;

    .cds--loading {
      left: calc(50% - 75px);
    }

    svg {
      margin-right: 5px;
    }
  }

  .cds--modal-container {
    max-width: 680px;
    box-shadow: none;
    max-height: none;
    height: auto;
    background-color: #fffffff2;
    overflow-y: scroll;
    max-height: calc(100vh - 60px);
  }

  .cds--checkbox-label-text p {
    padding-right: 0;
  }

  .messagePlaceholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 18px;
    text-align: center;

    .title {
      font-size: 1.75rem;
      color: inherit;
      width: 100%;
      font-weight: 700;
    }

    p {
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0;
    }
  }

  .successMessage {
    text-align: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: 20px 0 100px;
  }

  .logoContainer {
    width: 100%;
    min-width: 215px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px 20px 40px;

    img {
      display: block;
      max-width: 150px;
      height: auto;
    }
  }

  .formContainer {
    padding: 10px 126px 100px;

    .cds--text-input-wrapper {
      margin-top: 24px;
    }
  }

  .language {
    padding: 20px;
    display: flex;
    justify-content: end;

    .cds--list-box__wrapper {
      width: 90px;
    }

    .cds--list-box__selection {
      display: none;
    }

    .cds--list-box__field .cds--text-input {
      padding-right: 2rem;
    }
  }

  @media screen and (max-width: 767px) {
      .cds--modal-container {
        position: static;
        margin: 15px;
        max-height: calc(100vh - 30px);
      }

      .messagePlaceholder {
        padding: 0 20px;
      }

      .formContainer {
        padding: 26px 20px;
      }

      .partnerShip {
        padding: 20px;
      }

      .logoContainer {
        padding: 40px 20px 32px;
      }
    }
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  margin: 0 auto !important;
  padding: 0 !important;
  width: 100%;
  flex-direction: column;
`;

const PasswordChangeModal = ({
  register,
  loading,
  onSubmit,
  errors,
  rhfErrors,
  languages,
  language,
  setLanguage,
  validators,
  passwordChanged,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  return (
    <ModalManager open>
      <StyledComposedModal
        className="PasswordChangeModal"
        onClose={ e => e.preventDefault() }
        preventCloseOnClickOutside
        size="md"
        theme={ theme }
      >
        <div className="language">
          <NWComboBox
            label="lang"
            light
            name="lang"
            id="lang"
            initialSelectedItem={ language }
            onChange={ item => setLanguage(item.selectedItem) }
            items={ languages }
          />
        </div>
        <StyledModalBody theme={ theme }>
          <div className="logoContainer">
            <img
              src={ Logo2x }
              srcSet={ `${Logo} 149w, ${Logo2x} 297w` }
              sizes="(max-width: 600px) 149px, 297px"
              alt={ NETWORK_ONE_DISTRIBUTION }
            />
          </div>
          { !passwordChanged && (
            <div className="messagePlaceholder">
              <h3 className="title">{ t('profile.changePassword') }</h3>
              <p>{ t('profile.enterNewPassword') }</p>
            </div>
          )}
          { passwordChanged ? (
            <div className="successMessage">
              <h3>{ t('profile.passwordSuccessfullyChanged') }</h3>
              <h4>{ t('profile.redirectMessage') }</h4>
            </div>
          ) : (
            <div className="formContainer">
              <div className="responseMessage">
                <NWNotification errors={ errors } />
              </div>
              <NWTextInput
                light
                required
                type="password"
                labelText={ t('profile.newPassword') }
                { ...sharedProps('password') }
              />
              <NWTextInput
                light
                required
                type="password"
                labelText={ t('profile.confirmPassword') }
                { ...sharedProps('confirmPassword') }
              />
              <Button
                className="submit"
                type="submit"
                disabled={ loading || Object.keys(rhfErrors).length }
                onClick={ onSubmit }
                kind="danger"
              >
                { loading && <Loading withOverlay={ false } small active /> }
                { t('profile.changePassword') }
              </Button>
            </div>
          ) }
        </StyledModalBody>
      </StyledComposedModal>
    </ModalManager>
  );
};

PasswordChangeModal.propTypes = {
  register: func,
  loading: bool,
  passwordValue: string,
  onSubmit: func,
  errors: array,
  rhfErrors: object,
  languages: array,
  language: object,
  setLanguage: func,
  validators: object,
  passwordChanged: bool,
};

PasswordChangeModal.displayName = 'PasswordChangeModalComponent';

export default PasswordChangeModal;
